import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getMe, type OperatorMe } from '@/lib/me/api';

// Identity hook backed by `GET /v1/me`. The BFF is the source of truth for
// who the current operator is — works identically with real Auth0 logins,
// `dev.local.token`, and `dev.local.token` + `X-Dev-Operator-Override`.
//
// Replaces the previous `useAuth0().user[CLAIM_OPERATOR_URN]` reads at lock
// call sites, which broke whenever Auth0 was bypassed (no user object).
//
// `enabled` defaults to true — every existing caller lives inside an
// authenticated route (case detail), where the token is already present.
// Pre-auth callers (the always-mounted WsEventBridge, which renders before
// login) MUST pass `enabled: isAuthenticated`: a tokenless `GET /v1/me` 401s,
// the ky interceptor force-logs-out, and the app loops back to the login
// screen. Gating the call there keeps the query dormant until the session
// exists.

export function useOperator(opts?: { enabled?: boolean }): UseQueryResult<OperatorMe> {
  return useQuery<OperatorMe>({
    queryKey: ['auth', 'me'],
    queryFn: getMe,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    enabled: opts?.enabled ?? true,
  });
}
